// "For a customer" (23 Oct 2026, mock CallBook_1). Desk staff - the founder and the
// Onboarding agent - take customers' calls on the Call to book number and ring the owner or
// driver for them: before a Call, staff are asked "For myself / For a customer"; for a
// customer they give the customer's mobile and name (no OTP from the customer) and the
// enquiry is saved as the customer's (backend utils/onBehalf.js). Everyone else calls as
// before - the question is never shown to them. English, like every staff screen.
//
//   const ob = await askOnBehalf();          // null: not staff, or "For myself"
//   if (ob === ON_BEHALF_CANCELLED) return;  // they closed the sheet
//   body: { ...onBehalfBody(ob) }

import { useEffect, useState } from "react";
import { getAuth } from "../utils/auth";

export const ON_BEHALF_CANCELLED = "CANCELLED";
const STAFF = ["ADMIN", "SUPER_ADMIN", "ONBOARDING_AGENT"];
const EVENT = "sramika:on-behalf";
export const isDeskStaff = () => STAFF.includes(getAuth()?.role);
export const onBehalfBody = (ob) => (ob && ob !== ON_BEHALF_CANCELLED ? { onBehalf: ob } : {});

/** Resolves to null (not staff / for myself), { phone, name } (for a customer) or CANCELLED. */
export function askOnBehalf() {
  if (!isDeskStaff()) return Promise.resolve(null);
  return new Promise((resolve) => window.dispatchEvent(new CustomEvent(EVENT, { detail: { resolve } })));
}

export default function OnBehalfHost() {
  const [ask, setAsk] = useState(null);
  const [mode, setMode] = useState("customer");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    const open = (e) => { setAsk(() => e.detail.resolve); setMode("customer"); setPhone(""); setName(""); setErr(""); };
    window.addEventListener(EVENT, open);
    return () => window.removeEventListener(EVENT, open);
  }, []);
  if (!ask) return null;

  const done = (v) => { ask(v); setAsk(null); };
  const go = () => {
    if (mode === "self") return done(null);
    const d = phone.replace(/\D/g, "").slice(-10);
    if (!/^[6-9]\d{9}$/.test(d)) { setErr("Enter the customer's 10-digit mobile number."); return; }
    done({ phone: d, name: name.trim() || null });
  };
  const SEG = (on) => `flex-1 h-[36px] rounded-lg text-[12.5px] font-bold ${on ? "bg-white text-[#3F5E00] shadow-sm" : "text-[#475569]"}`;

  return (
    <div className="fixed inset-0 z-[300] flex items-end lg:items-center justify-center bg-black/40" onClick={() => done(ON_BEHALF_CANCELLED)}>
      <div role="dialog" aria-modal="true" aria-label="Call the driver" onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[440px] bg-white rounded-t-[20px] lg:rounded-[20px] p-4 space-y-3 pb-[max(16px,env(safe-area-inset-bottom))]">
        <p className="text-[15px] font-extrabold text-[#0F172A]">Call the driver</p>
        <div className="flex bg-[#F1F5F9] rounded-xl p-1">
          <button type="button" className={SEG(mode === "self")} onClick={() => setMode("self")}>For myself</button>
          <button type="button" className={SEG(mode === "customer")} onClick={() => setMode("customer")}>For a customer</button>
        </div>
        {mode === "customer" ? (
          <>
            <label className="block">
              <span className="text-[11.5px] font-bold text-[#475569]">Customer's mobile *</span>
              <input value={phone} onChange={(e) => { setPhone(e.target.value.replace(/[^\d+ ]/g, "").slice(0, 14)); setErr(""); }} inputMode="numeric" autoFocus
                placeholder="10-digit mobile" className="mt-1 w-full h-[44px] rounded-xl border border-[#CBD5E1] px-3 text-[14px] outline-none focus:border-[#7BAE00]" />
            </label>
            <label className="block">
              <span className="text-[11.5px] font-bold text-[#475569]">Customer's name</span>
              <input value={name} onChange={(e) => setName(e.target.value.slice(0, 60))} placeholder="Optional"
                className="mt-1 w-full h-[44px] rounded-xl border border-[#CBD5E1] px-3 text-[14px] outline-none focus:border-[#7BAE00]" />
            </label>
            <p className="text-[11.5px] text-[#64748B] leading-snug">No OTP for the customer. The enquiry is saved as theirs, marked as placed by you, and the owner gets the customer's name and number. Your phone rings the owner.</p>
          </>
        ) : (
          <p className="text-[12px] text-[#64748B] leading-snug">A call for yourself is a test call: no enquiry is saved.</p>
        )}
        {err && <p className="text-[12px] font-semibold text-[#B91C1C]">{err}</p>}
        <div className="flex gap-2">
          <button type="button" onClick={() => done(ON_BEHALF_CANCELLED)} className="h-[46px] px-4 rounded-xl border border-[#CBD5E1] text-[13px] font-bold text-[#334155]">Cancel</button>
          <button type="button" onClick={go} className="flex-1 h-[46px] rounded-xl bg-[#7BAE00] text-white text-[13.5px] font-extrabold">📞 Call the driver now</button>
        </div>
      </div>
    </div>
  );
}
